import React, { useState, useEffect } from 'react';
import { categoriesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { FolderTree, Plus, Edit2, Trash2, Tag } from 'lucide-react';
import { toast } from 'sonner';

const PRESET_COLORS = ['#121316', '#1E5A8A', '#1F7A5A', '#B89B5E', '#B3261E', '#4A1D2A', '#9A6B1A', '#5B616E'];

function toSlug(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function buildTree(categories) {
  const map = {};
  const roots = [];
  categories.forEach(c => { map[c.id] = { ...c, children: [] }; });
  categories.forEach(c => {
    if (c.parent_id && map[c.parent_id]) {
      map[c.parent_id].children.push(map[c.id]);
    } else {
      roots.push(map[c.id]);
    }
  });
  return roots;
}

function flattenTree(nodes, depth = 0) {
  const result = [];
  nodes.forEach(node => {
    result.push({ ...node, depth });
    if (node.children && node.children.length > 0) {
      flattenTree(node.children, depth + 1).forEach(n => result.push(n));
    }
  });
  return result;
}

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '', color: '#121316', icon: '', parent_id: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await categoriesAPI.list();
      setCategories(res.data.categories || []);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditId(null);
    setForm({ name: '', slug: '', description: '', color: '#121316', icon: '', parent_id: '' });
    setShowDialog(true);
  }

  function openEdit(cat) {
    setEditId(cat.id);
    setForm({ name: cat.name || '', slug: cat.slug || '', description: cat.description || '', color: cat.color || '#121316', icon: cat.icon || '', parent_id: cat.parent_id || '' });
    setShowDialog(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim()) { toast.error('Name and slug are required'); return; }
    setSaving(true);
    const payload = { name: form.name, slug: form.slug, description: form.description || null, color: form.color, icon: form.icon || null };
    const parentId = form.parent_id && form.parent_id !== 'none' ? form.parent_id : null;
    try {
      if (editId) {
        await categoriesAPI.update(editId, payload);
        await categoriesAPI.setParent(editId, parentId);
        toast.success('Category updated');
      } else {
        const res = await categoriesAPI.create(payload);
        if (parentId) await categoriesAPI.setParent(res.data.id, parentId);
        toast.success('Category created');
      }
      setShowDialog(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this category? Child categories will be unlinked.')) return;
    try {
      await categoriesAPI.delete(id);
      toast.success('Category deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  }

  const tree = buildTree(categories);
  const flatList = flattenTree(tree);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderTree className="w-6 h-6 text-[#B89B5E]" /> Categories
          </h1>
          <p className="text-sm text-[#5B616E] mt-1">Hierarchical category tree</p>
        </div>
        <Button onClick={openCreate} className="bg-[#121316] text-white hover:bg-[#1B1D22]">
          <Plus className="w-4 h-4 mr-2" /> New Category
        </Button>
      </div>

      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <FolderTree className="w-4 h-4" /> Category Tree ({categories.length} total)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-10 rounded-lg" />)}
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-8 text-[#5B616E]">
              <Tag className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>No categories yet. Create your first category.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E7E0D6]">
              {flatList.map(cat => (
                <div
                  key={cat.id}
                  className="flex items-center gap-2 py-2 hover:bg-[#FBF7EF] transition-colors"
                  style={{ paddingLeft: `${(cat.depth * 20) + 12}px` }}
                >
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: cat.color || '#5B616E' }} />
                  <span className="font-medium text-sm flex-1 truncate">{cat.name}</span>
                  {cat.depth > 0 && <Badge variant="outline" className="text-[9px]">sub</Badge>}
                  <span className="text-[10px] text-[#5B616E] font-mono">{cat.slug}</span>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(cat)}>
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(cat.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {categories.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.slice(0, 8).map(cat => (
            <Card key={cat.id} className="bg-white border-[#E7E0D6] cursor-pointer hover:border-[#B89B5E] transition-colors" onClick={() => openEdit(cat)}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 rounded-full" style={{ background: cat.color || '#5B616E' }} />
                  <span className="font-medium text-sm truncate">{cat.name}</span>
                </div>
                <p className="text-[10px] text-[#5B616E]">{cat.slug}</p>
                {cat.parent_id && <Badge variant="outline" className="text-[9px] mt-1">sub-category</Badge>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Category' : 'New Category'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Name *</label>
                <Input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: f.slug || toSlug(e.target.value) }))}
                  required
                  placeholder="Technology"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Slug *</label>
                <Input
                  value={form.slug}
                  onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                  required
                  placeholder="technology"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Parent category</label>
              <Select value={form.parent_id || 'none'} onValueChange={v => setForm(f => ({ ...f, parent_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Top-level (no parent)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Top-level —</SelectItem>
                  {categories.filter(c => c.id !== editId).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} className="w-10 h-10 rounded border cursor-pointer" />
                <div className="flex gap-1 flex-wrap">
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, color: c }))}
                      className="w-6 h-6 rounded-full border-2 transition-all"
                      style={{ background: c, borderColor: form.color === c ? '#B89B5E' : 'transparent' }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Icon (emoji or name)</label>
              <Input value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="📰" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#121316] text-white hover:bg-[#1B1D22]">
                {saving ? 'Saving…' : editId ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
